(() => {
  const doc = document;
  const body = doc.body;
  const overlay = () => doc.querySelector('[data-overlay]');
  const moneyFormat = window.themeMoneyFormat || '${{amount}}';

  const formatMoney = (cents) => {
    const value = Number(cents || 0);
    const amount = (value / 100).toFixed(2);
    const amountNoDecimals = Math.round(value / 100).toString();
    return moneyFormat
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  };

  const showOverlay = () => {
    const el = overlay();
    if (el) el.hidden = false;
    body.classList.add('drawer-open');
  };

  const hideOverlayIfClear = () => {
    if (doc.querySelector('.is-open[data-cart-drawer], .is-open[data-mobile-menu], .is-open[data-filter-panel], .wishlist-drawer.is-open')) return;
    const el = overlay();
    if (el) el.hidden = true;
    body.classList.remove('drawer-open');
  };

  const closeDrawers = () => {
    doc.querySelectorAll('[data-cart-drawer].is-open, [data-mobile-menu].is-open, [data-filter-panel].is-open, .wishlist-drawer.is-open').forEach((el) => {
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
    });
    hideOverlayIfClear();
  };

  const openDrawer = (el) => {
    if (!el) return;
    closeDrawers();
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    showOverlay();
    const focusTarget = el.querySelector('button, a, input');
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  };

  const getWishlist = () => {
    try { return JSON.parse(localStorage.getItem('urban-vibes-wishlist') || '[]'); }
    catch (_) { return []; }
  };

  const saveWishlist = (items) => {
    localStorage.setItem('urban-vibes-wishlist', JSON.stringify(items));
    syncWishlist();
  };

  const syncWishlist = () => {
    const items = getWishlist();
    doc.querySelectorAll('[data-wishlist-count]').forEach((el) => { el.textContent = items.length; });
    doc.querySelectorAll('[data-wishlist-button]').forEach((button) => {
      const active = items.some((item) => item.handle === button.dataset.productHandle);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };

  const ensureWishlistDrawer = () => {
    let drawer = doc.querySelector('.wishlist-drawer');
    if (!drawer) {
      drawer = doc.createElement('aside');
      drawer.className = 'wishlist-drawer';
      drawer.setAttribute('aria-hidden', 'true');
      drawer.innerHTML = '<div class="cart-drawer__head"><h2>WISHLIST</h2><button class="icon-button" type="button" aria-label="Close wishlist" data-wishlist-close>×</button></div><div class="wishlist-drawer__items" data-wishlist-items></div>';
      body.appendChild(drawer);
    }
    const itemsEl = drawer.querySelector('[data-wishlist-items]');
    const items = getWishlist();
    itemsEl.innerHTML = items.length
      ? items.map((item) => `<div class="wishlist-drawer__item"><a href="${item.url}">${item.title}</a><button type="button" data-wishlist-remove="${item.handle}">Remove</button></div>`).join('')
      : '<div class="empty-state"><p>Your wishlist is empty.</p></div>';
    return drawer;
  };

  const refreshCartDrawer = async () => {
    const sectionUrl = `${window.themeRoutes.root}?section_id=cart-drawer`;
    const cartUrl = `${window.themeRoutes.cart}.js`;
    const [sectionResponse, cartResponse] = await Promise.all([fetch(sectionUrl), fetch(cartUrl, { headers: { Accept: 'application/json' } })]);
    const html = await sectionResponse.text();
    const cart = await cartResponse.json();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const freshDrawer = parsed.querySelector('[data-cart-drawer]');
    const currentDrawer = doc.querySelector('[data-cart-drawer]');
    if (freshDrawer && currentDrawer) currentDrawer.replaceWith(freshDrawer);
    doc.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
    return doc.querySelector('[data-cart-drawer]');
  };

  const addToCart = async (form) => {
    const submit = form.querySelector('[type="submit"]');
    const error = form.querySelector('.form-error');
    if (submit) submit.disabled = true;
    if (error) error.hidden = true;
    try {
      const response = await fetch(`${window.themeRoutes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to add this item.');
      const drawer = await refreshCartDrawer();
      openDrawer(drawer);
    } catch (err) {
      if (error) { error.textContent = err.message; error.hidden = false; }
    } finally {
      if (submit) submit.disabled = false;
    }
  };

  const activateMedia = (root, mediaId) => {
    root.querySelectorAll('[data-media-item]').forEach((item) => {
      const active = String(item.dataset.mediaItem) === String(mediaId);
      item.hidden = !active;
      item.classList.toggle('is-active', active);
    });
    root.querySelectorAll('[data-media-thumb]').forEach((thumb) => thumb.classList.toggle('is-active', String(thumb.dataset.mediaThumb) === String(mediaId)));
  };

  const initProduct = (root) => {
    if (!root || root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const variantsNode = root.querySelector('[data-product-variants]');
    if (!variantsNode) return;
    let variants = [];
    try { variants = JSON.parse(variantsNode.textContent); } catch (_) { return; }

    const updateVariant = () => {
      const selectedOptions = Array.from(root.querySelectorAll('[data-option-index]')).map((group) => {
        const checked = group.querySelector('[data-option-input]:checked');
        const label = group.querySelector('[data-selected-option]');
        if (label && checked) label.textContent = checked.value;
        return checked ? checked.value : null;
      });
      const variant = variants.find((item) => item.options.every((option, index) => option === selectedOptions[index]));
      const idInput = root.querySelector('[data-variant-id]');
      const submit = root.querySelector('[data-add-to-cart]');
      const submitText = root.querySelector('[data-add-to-cart-text]');
      if (!variant) {
        if (submit) submit.disabled = true;
        if (submitText) submitText.textContent = 'UNAVAILABLE';
        return;
      }
      if (idInput) idInput.value = variant.id;
      const priceEl = root.querySelector('[data-price]');
      if (priceEl) {
        priceEl.classList.toggle('price--on-sale', variant.compare_at_price && variant.compare_at_price > variant.price);
        priceEl.innerHTML = `<span class="price__current">${formatMoney(variant.price)}</span>${variant.compare_at_price && variant.compare_at_price > variant.price ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : ''}`;
      }
      if (submit) submit.disabled = !variant.available;
      if (submitText) submitText.textContent = variant.available ? `ADD TO CART - ${formatMoney(variant.price)}` : 'SOLD OUT';
      if (variant.featured_media && variant.featured_media.id) activateMedia(root, variant.featured_media.id);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    root.querySelectorAll('[data-option-input]').forEach((input) => input.addEventListener('change', updateVariant));
    root.querySelectorAll('[data-media-thumb]').forEach((thumb) => thumb.addEventListener('click', () => activateMedia(root, thumb.dataset.mediaThumb)));
  };

  const initRecommendations = (root) => {
    if (!root || root.dataset.loaded) return;
    root.dataset.loaded = 'true';
    fetch(root.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const fresh = parsed.querySelector('[data-recommendations-root]');
        if (fresh && fresh.innerHTML.trim()) root.innerHTML = fresh.innerHTML;
        syncWishlist();
      })
      .catch(() => {});
  };

  const init = (scope = doc) => {
    scope.querySelectorAll('[data-product-root]').forEach(initProduct);
    scope.querySelectorAll('[data-recommendations-root]').forEach(initRecommendations);
    syncWishlist();
  };

  doc.addEventListener('submit', (event) => {
    const form = event.target.closest('.js-product-form');
    if (!form) return;
    event.preventDefault();
    addToCart(form);
  });

  doc.addEventListener('click', (event) => {
    const target = event.target;
    const menuOpen = target.closest('[data-menu-open]');
    if (menuOpen) openDrawer(doc.querySelector('[data-mobile-menu]'));
    if (target.closest('[data-menu-close]') || target.closest('[data-cart-close]') || target.closest('[data-filter-close]') || target.closest('[data-wishlist-close]')) closeDrawers();

    const cartOpen = target.closest('[data-cart-open]');
    if (cartOpen) { event.preventDefault(); openDrawer(doc.querySelector('[data-cart-drawer]')); }
    const filterOpen = target.closest('[data-filter-open]');
    if (filterOpen) openDrawer(doc.querySelector('[data-filter-panel]'));
    const wishlistOpen = target.closest('[data-wishlist-open]');
    if (wishlistOpen) openDrawer(ensureWishlistDrawer());

    const wishlistButton = target.closest('[data-wishlist-button]');
    if (wishlistButton) {
      event.preventDefault();
      const items = getWishlist();
      const index = items.findIndex((item) => item.handle === wishlistButton.dataset.productHandle);
      if (index >= 0) items.splice(index, 1);
      else items.push({ handle: wishlistButton.dataset.productHandle, title: wishlistButton.dataset.productTitle, url: wishlistButton.dataset.productUrl });
      saveWishlist(items);
    }
    const removeWishlist = target.closest('[data-wishlist-remove]');
    if (removeWishlist) {
      saveWishlist(getWishlist().filter((item) => item.handle !== removeWishlist.dataset.wishlistRemove));
      openDrawer(ensureWishlistDrawer());
    }

    const quantityMinus = target.closest('[data-quantity-minus]');
    const quantityPlus = target.closest('[data-quantity-plus]');
    if (quantityMinus || quantityPlus) {
      const control = target.closest('.quantity-control');
      const input = control && control.querySelector('[data-quantity-input]');
      if (input) input.value = Math.max(1, Number(input.value || 1) + (quantityPlus ? 1 : -1));
    }

    const sliderPrev = target.closest('[data-slider-prev]');
    const sliderNext = target.closest('[data-slider-next]');
    if (sliderPrev || sliderNext) {
      const shell = target.closest('.slider-shell');
      const track = shell && shell.querySelector('[data-slider-track]');
      if (track) track.scrollBy({ left: (sliderNext ? 1 : -1) * track.clientWidth * .8, behavior: 'smooth' });
    }

    const mediaThumb = target.closest('[data-media-thumb]');
    if (mediaThumb) activateMedia(mediaThumb.closest('[data-product-root]'), mediaThumb.dataset.mediaThumb);
    const zoomOpen = target.closest('[data-zoom-open]');
    if (zoomOpen) {
      const root = zoomOpen.closest('[data-product-root]');
      const dialog = root.querySelector('[data-zoom-dialog]');
      const activeImage = root.querySelector('[data-media-item].is-active img');
      if (dialog && activeImage) { dialog.querySelector('[data-zoom-content]').innerHTML = ''; dialog.querySelector('[data-zoom-content]').appendChild(activeImage.cloneNode(true)); dialog.showModal(); }
    }
    const zoomClose = target.closest('[data-zoom-close]');
    if (zoomClose) zoomClose.closest('dialog').close();
    const sizeOpen = target.closest('[data-size-guide-open]');
    if (sizeOpen) sizeOpen.closest('[data-product-root]').querySelector('[data-size-guide-dialog]').showModal();
    const sizeClose = target.closest('[data-size-guide-close]');
    if (sizeClose) sizeClose.closest('dialog').close();

    const reviewOpen = target.closest('[data-review-form-open]');
    if (reviewOpen) reviewOpen.closest('[data-review-section]').querySelector('[data-review-form-dialog]').showModal();
    const reviewClose = target.closest('[data-review-form-close]');
    if (reviewClose) reviewClose.closest('dialog').close();
    const reviewFilter = target.closest('[data-review-filter]');
    if (reviewFilter) {
      const section = reviewFilter.closest('[data-review-section]');
      section.querySelectorAll('[data-review-filter]').forEach((button) => button.classList.toggle('is-active', button === reviewFilter));
      section.querySelectorAll('[data-review-item]').forEach((item) => {
        const filter = reviewFilter.dataset.reviewFilter;
        const visible = filter === 'all' || (filter === 'photos' && item.dataset.reviewPhotos === 'true') || item.dataset.reviewRating === filter;
        item.hidden = !visible;
        if (visible) item.classList.remove('is-review-hidden');
      });
    }
    const reviewMore = target.closest('[data-review-load-more]');
    if (reviewMore) { reviewMore.closest('[data-review-section]').querySelectorAll('.is-review-hidden').forEach((item) => item.classList.remove('is-review-hidden')); reviewMore.hidden = true; }
  });

  doc.addEventListener('change', (event) => {
    const sort = event.target.closest('[data-sort-select]');
    if (sort) { const url = new URL(window.location.href); url.searchParams.set('sort_by', sort.value); window.location.assign(url.toString()); }
  });

  const pageOverlay = overlay();
  if (pageOverlay) pageOverlay.addEventListener('click', closeDrawers);
  doc.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeDrawers(); });
  doc.addEventListener('shopify:section:load', (event) => init(event.target));
  init();
})();
// Arrow navigation for product gallery
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-product-gallery]').forEach(gallery => {
    const mediaItems = [...gallery.querySelectorAll('[data-media-item]')];
    const thumbs = [...gallery.querySelectorAll('[data-media-thumb]')];
    if (mediaItems.length < 2) return;

    function showMedia(id) {
      mediaItems.forEach(el => {
        const active = el.dataset.mediaItem === String(id);
        el.classList.toggle('is-active', active);
        el.hidden = !active;
      });
      thumbs.forEach(btn => btn.classList.toggle('is-active', btn.dataset.mediaThumb === String(id)));
    }

    function currentIndex() {
      return mediaItems.findIndex(el => el.classList.contains('is-active'));
    }

    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => {
      const idx = (currentIndex() - 1 + mediaItems.length) % mediaItems.length;
      showMedia(mediaItems[idx].dataset.mediaItem);
    });

    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => {
      const idx = (currentIndex() + 1) % mediaItems.length;
      showMedia(mediaItems[idx].dataset.mediaItem);
    });
  });
});

